import type { RefObject } from 'react';
import { memo, useRef } from 'react';

import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers';
import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { useSortable } from '@dnd-kit/react/sortable';
import { Eye, EyeOff, GripVertical } from 'lucide-react';

import { SymbolContent } from '@/features/dashboard/components/AppForm/AppForm/components/ItemSelection/SymbolContent';
import type { TabSymbol } from '@/features/dashboard/components/AppForm/AppForm/context/types';

interface SortableItemProps {
  item: TabSymbol;
  sourceListId: string;
  itemIndex: number;
  container: RefObject<HTMLDivElement | null>;
}

const SortableItemComponent = ({ item, sourceListId, itemIndex, container }: SortableItemProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLButtonElement | null>(null);
  useSortable({
    id: item.id,
    index: itemIndex,
    group: sourceListId,
    element: ref,
    handle: handleRef,
    type: 'item',
    accept: 'item',
    modifiers: [RestrictToVerticalAxis, RestrictToElement.configure({ element: container.current })]
  });

  const isActive = sourceListId === 'active';

  return (
    <div
      ref={ref}
      className='relative my-2 rounded-lg border border-border bg-card py-2.5 pr-3 pl-8 transition data-[muted=true]:text-muted-foreground'
      data-sortable-item
      data-muted={!isActive}
    >
      <div className='flex items-center gap-3'>
        <button
          ref={handleRef}
          type='button'
          aria-label='Drag to reorder'
          className='absolute inset-y-0 left-0 flex w-8 cursor-grab items-center justify-center'
        >
          <GripVertical
            aria-hidden='true'
            className='size-4'
          />
        </button>
        {isActive ? (
          <Eye
            aria-label='Displayed'
            className='size-4 shrink-0 text-primary'
          />
        ) : (
          <EyeOff
            aria-label='Not displayed'
            className='size-4 shrink-0 text-muted-foreground'
          />
        )}
        <div className='min-w-0 flex-1'>
          <SymbolContent
            index={itemIndex}
            symbol={item}
          />
        </div>
      </div>
    </div>
  );
};

export const SortableItem = memo(SortableItemComponent);
